// Google OAuth (authorization-code + PKCE) client helpers for the portal
// sign-in hop. The portal redirects to Google, receives a `code` back at /auth,
// and POSTs it to POST /api/signin (which holds the client secret, exchanges the
// code, and sets the .coffeechats.me cookie). This module is the pure/DOM half:
// a PKCE pair, a CSRF `state` nonce, the authorize URL, and the sessionStorage
// stash that carries the in-flight record across the same-origin redirect
// round-trip. No network here (the exchange lives in auth.js) so it stays
// unit-testable.

const FLOW_KEY = 'cc.portal.oauth';
const GOOGLE_AUTH_ENDPOINT = 'https://accounts.google.com/o/oauth2/v2/auth';

// base64url (RFC 4648 §5, no padding) of a byte array.
function base64Url(bytes) {
  let bin = '';
  for (let i = 0; i < bytes.length; i += 1) {
    bin += String.fromCharCode(bytes[i]);
  }
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * A URL-safe random token. 32 bytes → 43 base64url chars. Used for both the
 * PKCE verifier and the CSRF `state` nonce.
 * @param {number} bytes
 * @returns {string}
 */
export function randomUrlToken(bytes = 32) {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return base64Url(buf);
}

/**
 * PKCE pair: a high-entropy verifier and its S256 challenge (what Google sees).
 * @returns {Promise<{codeVerifier: string, codeChallenge: string}>}
 */
export async function createPkcePair() {
  const codeVerifier = randomUrlToken(32);
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
  const codeChallenge = base64Url(new Uint8Array(digest));
  return { codeVerifier, codeChallenge };
}

/**
 * Build the Google authorize URL for the code + PKCE flow. Identity only — no
 * `access_type=offline` — because the server treats Google tokens as optional
 * (it only needs the id_token). `redirect_uri` is static so Google's exact-match
 * check passes; the slug rides sessionStorage, not the URL.
 * @param {{clientId: string, redirectUri: string, state: string, codeChallenge: string}} p
 * @returns {string}
 */
export function buildGoogleAuthorizeUrl({ clientId, redirectUri, state, codeChallenge }) {
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid profile email',
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    prompt: 'select_account',
    include_granted_scopes: 'true',
  });
  return `${GOOGLE_AUTH_ENDPOINT}?${params.toString()}`;
}

/**
 * Stash the in-flight record so the return leg (same origin, after Google) can
 * verify `state` and recover the slug + PKCE verifier. sessionStorage survives
 * the top-level redirect round-trip within the tab.
 * @param {{state: string, codeVerifier: string, slug: string, redirectUri: string}} flow
 */
export function saveOAuthFlow(flow) {
  sessionStorage.setItem(FLOW_KEY, JSON.stringify(flow));
}

/** Read the stashed flow record, or null if absent/corrupt. */
export function loadOAuthFlow() {
  const raw = sessionStorage.getItem(FLOW_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Drop the stashed flow record (single-use). */
export function clearOAuthFlow() {
  sessionStorage.removeItem(FLOW_KEY);
}
